import clsx from 'clsx';

import { Badge, BadgeVariant } from '../badge';
import { OmniumStore } from '@fagmobler/omnium';
import { EyeIcon } from '../icons';
import { MouseEvent } from 'react';
import { twMerge } from 'tailwind-merge';
import type { FagmoblerAlgoliaProduct } from '@fagmobler/types';

function InfoIcon({ className }: { className: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12Z"
        fill="currentColor"
      />
      <path
        d="M12 10C12.5523 10 13 10.4477 13 11V17C13 17.5523 12.5523 18 12 18C11.4477 18 11 17.5523 11 17V11C11 10.4477 11.4477 10 12 10Z"
        fill="currentColor"
      />
      <path
        d="M13.5 7.5C13.5 8.32843 12.8284 9 12 9C11.1716 9 10.5 8.32843 10.5 7.5C10.5 6.67157 11.1716 6 12 6C12.8284 6 13.5 6.67157 13.5 7.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

function shortenStoreName(store: OmniumStore) {
  if (!store || !store.name) return null;
  if (store.name.includes('Fagmøbler')) {
    return store.name.replace('Fagmøbler', '').trim();
  }
  return store?.name;
}

type StockIndicatorsProps = {
  stock?: {
    display: number;
    stock: number;
  } | null;
  cartQuanitity?: number;
  inline?: boolean;
  className?: string;
  store?: OmniumStore | undefined;
  onStoreClick?(event: MouseEvent): void;
  notAvailable?: boolean;
  isBackorder?: boolean;
  deliveryTime?: string;
  display?: 'cart' | 'product';
  algoliaProduct?: FagmoblerAlgoliaProduct;
};

type StatusState = {
  type: 'badge' | 'icon';
  text: string;
  active: boolean;
  variant?: string;
  icon?: any;
  additionalClassName?: string;
  display: string[];
  override?: boolean;
};

export function StockIndicators({
  stock,
  cartQuanitity,
  inline,
  className,
  store,
  onStoreClick,
  notAvailable,
  isBackorder,
  deliveryTime,
  display = 'product',
  algoliaProduct,
}: StockIndicatorsProps) {
  const inStock = stock?.stock ?? 0;
  const onDisplay = stock?.display ?? 0;
  const isAvailable = !isBackorder && !notAvailable;

  const stockCount =
    stock?.stock && stock?.stock > 20 ? '20+' : stock?.stock ?? 0;
  const storeName = (store && shortenStoreName(store)) || 'butikk';

  const isOutdoorFurniture = algoliaProduct?.parentId == 'AG1501' || false;
  const isOutdoorFurnitureStore = !store?.properties?.find(
    (prop) => prop.key === 'blockOutdoorFurniture' && prop.value === 'true'
  );

  const statusStates: StatusState[] = [
    {
      type: 'badge',
      variant: 'successDark',
      text: `På lager (${stockCount})`,
      active: inStock > 0,
      display: ['product'],
    },
    {
      type: 'badge',
      variant: 'warningNeutral',
      text: 'Bestillingsvare',
      active:
        (!inStock && isAvailable && !deliveryTime) ||
        (!deliveryTime && (cartQuanitity ?? 0) > inStock && inStock > 0),
      display: ['product', 'cart'],
    },
    {
      type: 'badge',
      variant: 'warningNeutral',
      text: `Estimert leveringstid: ${deliveryTime}`,
      active:
        (!inStock && isAvailable && !!deliveryTime) ||
        (!!deliveryTime && (cartQuanitity ?? 0) > inStock && inStock > 0),
      display: ['product', 'cart'],
    },
    {
      type: 'badge',
      variant: 'primaryDark',
      text: 'Utsolgt',
      active: inStock < 1 && !isAvailable,
      display: ['product'],
    },
    {
      type: 'icon',
      icon: EyeIcon,
      text: `Utstilt i ${storeName}`,
      active: onDisplay > 0,
      display: ['product'],
    },
    {
      type: 'badge',
      variant: 'successDark',
      text: 'Alle varer på lager',
      active: inStock > 0 && inStock >= (cartQuanitity ?? 0),
      display: ['cart'],
    },
    {
      type: 'icon',
      icon: InfoIcon,
      text: `${stockCount} av ${cartQuanitity} på lager. Resterende har bestillingstid`,
      active: (cartQuanitity ?? 0) > inStock && inStock > 0,
      display: ['cart'],
    },
    {
      type: 'badge',
      variant: 'primaryDark',
      text: 'Hagemøbler kan ikke bestilles på nett hos denne butikken',
      active: isOutdoorFurniture && !isOutdoorFurnitureStore,
      display: ['product', 'cart'],
      override: true,
    },
  ];

  const currentStockState = statusStates
    .filter((state) => state.active && state.display.includes(display))
    .reduce((acc: StatusState[], state: StatusState) => {
      // If we find an override state, only keep override states
      if (state.override) {
        return acc.filter((s) => s.override).concat(state);
      }
      // If we already have override states, don't add non-override states
      if (acc.some((s) => s.override)) {
        return acc;
      }
      return [...acc, state];
    }, [] as StatusState[]);

  return (
    <div
      className={clsx('body-xs', className, {
        'flex flex-col gap-1': !inline,
        'flex items-center gap-x-4': inline,
      })}
    >
      {currentStockState.map((state, index) => (
        <span
          key={index}
          className={twMerge(
            clsx(
              'flex gap-1 items-center leading-snug',
              state.additionalClassName
            )
          )}
        >
          {state.type === 'badge' && state.variant && (
            <Badge
              size="xs"
              variant={state.variant as BadgeVariant}
              className={clsx('h-3 w-3 gap-1 flex-shrink-0')}
            />
          )}
          {state.type === 'icon' && state.icon && (
            <state.icon className="w-4 h-4 text-neutral-70 gap-1" />
          )}
          <span>{state.text}</span>
        </span>
      ))}
    </div>
  );
}
